import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import { FileSearch, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { Tooltip } from "@/components/ui/Tooltip";
import { useAuthStore } from "@/store/auth";
import { useEngAronMeta, useProjectsPicker } from "../api/engAronApi";
import {
  useCreateReview,
  useMyReviews,
  useReviews,
  useStartReview,
} from "../api/reviewsApi";
import { ReviewModeInfo, useReviewModeOptions } from "../components/ReviewModeInfo";
import {
  DOC_TYPE_LABEL,
  REVIEW_MODES,
  REVIEW_STATUS_COLOR,
  REVIEW_STATUS_DESC,
  REVIEW_STATUS_LABEL,
  WORKFLOW_STATE_COLOR,
  WORKFLOW_STATE_DESC,
  WORKFLOW_STATE_LABEL,
} from "../utils";
import type { EngReview, ReviewStatus, ReviewWorkflowState } from "../types";

/* Jedna pozycja legendy: badge + liczba recenzji w tym stanie + objaśnienie.
   Kliknięcie włącza filtr listy po tym stanie; ponowne kliknięcie go zdejmuje. */
function LegendChip({
  color,
  label,
  count,
  desc,
  active,
  onClick,
}: {
  color: "gray" | "blue" | "green" | "red" | "yellow" | "orange";
  label: string;
  count: number;
  desc: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? "Kliknij, aby zdjąć filtr" : "Kliknij, aby przefiltrować listę"}
      className={clsx(
        "block w-full border p-2 text-left transition-colors",
        active
          ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
          : "border-[var(--color-border)] bg-[var(--color-bg-surface)] hover:border-[var(--color-border-strong)]",
      )}
    >
      <span className="flex items-center gap-2">
        <Badge color={color}>{label}</Badge>
        <span
          className={clsx(
            "text-xs font-semibold tabular-nums",
            count > 0
              ? "text-[var(--color-text-primary)]"
              : "text-[var(--color-text-muted)]",
          )}
        >
          {count}
        </span>
        {active && <X size={12} className="ml-auto text-[var(--color-text-muted)]" />}
      </span>
      <span className="mt-1 block text-xs leading-snug text-[var(--color-text-muted)]">
        {desc}
      </span>
    </button>
  );
}

/* Legenda badge'y widocznych na liście recenzji: stan obiegu (kto trzyma
   opracowanie) i status analizy AI. Liczniki liczone z pełnej, niefiltrowanej
   listy — pokazują, ile takich recenzji jest AKTUALNIE w systemie. */
function StatusLegend({
  reviews,
  stateFilter,
  statusFilter,
  onStateFilter,
  onStatusFilter,
}: {
  reviews: EngReview[];
  stateFilter: string;
  statusFilter: string;
  onStateFilter: (v: string) => void;
  onStatusFilter: (v: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const counts = useMemo(() => {
    const byState: Partial<Record<ReviewWorkflowState, number>> = {};
    const byStatus: Partial<Record<ReviewStatus, number>> = {};
    for (const r of reviews) {
      byState[r.review_state] = (byState[r.review_state] ?? 0) + 1;
      byStatus[r.status] = (byStatus[r.status] ?? 0) + 1;
    }
    return { byState, byStatus };
  }, [reviews]);

  return (
    <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
      >
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          Legenda statusów
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          {open ? "zwiń" : "rozwiń"}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-[var(--color-border)] p-3">
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Stan obiegu — kto aktualnie „trzyma" opracowanie
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(Object.keys(WORKFLOW_STATE_LABEL) as ReviewWorkflowState[]).map((s) => (
                <LegendChip
                  key={s}
                  color={WORKFLOW_STATE_COLOR[s]}
                  label={WORKFLOW_STATE_LABEL[s]}
                  count={counts.byState[s] ?? 0}
                  desc={WORKFLOW_STATE_DESC[s]}
                  active={stateFilter === s}
                  onClick={() => onStateFilter(stateFilter === s ? "" : s)}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Status analizy AI
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(Object.keys(REVIEW_STATUS_LABEL) as ReviewStatus[]).map((s) => (
                <LegendChip
                  key={s}
                  color={REVIEW_STATUS_COLOR[s]}
                  label={REVIEW_STATUS_LABEL[s]}
                  count={counts.byStatus[s] ?? 0}
                  desc={REVIEW_STATUS_DESC[s]}
                  active={statusFilter === s}
                  onClick={() => onStatusFilter(statusFilter === s ? "" : s)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const meta = useEngAronMeta();
  const projects = useProjectsPicker("");
  const createReview = useCreateReview();
  const startReview = useStartReview();

  const [project, setProject] = useState("");
  const [label, setLabel] = useState("");
  const [branch, setBranch] = useState("telecom");
  const [docType, setDocType] = useState("pb");
  const [mode, setMode] = useState("standard");
  const [notes, setNotes] = useState<string[]>([]);
  const [noteInput, setNoteInput] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [runNow, setRunNow] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function addNote() {
    const v = noteInput.trim();
    if (!v) return;
    setNotes((prev) => [...prev, v]);
    setNoteInput("");
  }

  const modeOptions = useReviewModeOptions();
  const projectOptions = useMemo(
    () =>
      (projects.data ?? []).map((p) => ({
        value: p.id,
        label: p.client_name ? `${p.name} (${p.client_name})` : p.name,
      })),
    [projects.data],
  );

  const busy = createReview.isPending || startReview.isPending;

  async function submit() {
    setError(null);
    if (!label.trim() || files.length === 0) {
      setError("Podaj nazwę opracowania i dodaj PDF.");
      return;
    }
    // Domykamy niedodany, wpisany punkt wskazówek.
    const allNotes = noteInput.trim() ? [...notes, noteInput.trim()] : notes;
    try {
      const review = await createReview.mutateAsync({
        project: project || null,
        label: label.trim(),
        branch,
        doc_type: docType,
        mode,
        checker_notes: allNotes.map((n) => `- ${n}`).join("\n"),
        file: files[0],
      });
      if (runNow) await startReview.mutateAsync(review.id);
      onClose();
      setFiles([]);
      setLabel("");
      setNotes([]);
      setNoteInput("");
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })
        ?.response?.data?.errors?.detail;
      setError(detail ?? "Nie udało się wgrać opracowania.");
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Wgraj opracowanie do recenzji" size="lg">
      <div className="space-y-4">
        <SearchableSelect
          label="Projekt (przedsięwzięcie) — opcjonalnie"
          value={project}
          onChange={(e) => setProject(e.target.value)}
          options={[
            { value: "", label: "— bez przedsięwzięcia (wiedza globalna/branżowa) —" },
            ...projectOptions,
          ]}
          placeholder="— bez przedsięwzięcia —"
        />
        <Input
          label="Nazwa opracowania"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="np. PB-KPO-OSJAKÓW-4"
        />
        <div className="grid grid-cols-3 gap-3">
          <Select
            label="Branża"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            options={meta.data?.branches ?? []}
          />
          <Select
            label="Typ opracowania"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            options={meta.data?.doc_types ?? []}
          />
          <Select
            label="Tryb analizy"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            options={modeOptions}
          />
        </div>
        <p className="text-xs text-[var(--color-text-muted)] -mt-2">
          {REVIEW_MODES.find((m) => m.value === mode)?.hint}
        </p>
        <ReviewModeInfo mode={mode} />

        {/* Wskazówki dla recenzenta — lista punktów */}
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            Wskazówki dla recenzenta (opcjonalne) — dodawaj punkt po punkcie
          </label>
          <div className="flex gap-2">
            <Input
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addNote();
                }
              }}
              placeholder="np. zwróć uwagę na długości — zmieniała się trasa"
              className="flex-1"
            />
            <Button type="button" variant="secondary" onClick={addNote}>
              Dodaj
            </Button>
          </div>
          {notes.length > 0 && (
            <ul className="mt-2 space-y-1">
              {notes.map((n, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-2 text-sm bg-[var(--color-bg-elevated)] border border-[var(--color-border)] px-2 py-1"
                >
                  <span className="text-[var(--color-text-secondary)]">• {n}</span>
                  <button
                    type="button"
                    onClick={() => setNotes((prev) => prev.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <label className="block text-xs text-[var(--color-text-muted)] mb-1">
            PDF opracowania
          </label>
          <FileDropZone files={files} onFilesChange={setFiles} accept=".pdf,application/pdf" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={runNow}
            onChange={(e) => setRunNow(e.target.checked)}
          />
          Uruchom analizę od razu po wgraniu
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Anuluj
          </Button>
          <Button onClick={submit} loading={busy}>
            {runNow ? "Wgraj i analizuj" : "Wgraj"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReviewRow({ review }: { review: EngReview }) {
  const startReview = useStartReview();
  return (
    <Link
      to={`/eng-aron/reviews/${review.id}`}
      className="block border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-4 hover:border-[var(--color-primary)] transition-colors"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-[var(--color-text-primary)] truncate">
              {review.label}
            </span>
            <Badge color="gray">v{review.version}</Badge>
            <Badge color="blue">{DOC_TYPE_LABEL[review.doc_type]}</Badge>
            {review.review_state !== "draft" && (
              <Tooltip content={WORKFLOW_STATE_DESC[review.review_state]}>
                <Badge color={WORKFLOW_STATE_COLOR[review.review_state]}>
                  {WORKFLOW_STATE_LABEL[review.review_state]}
                </Badge>
              </Tooltip>
            )}
          </div>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {review.project_name ?? "bez przedsięwzięcia"} · {review.uploaded_by_name ?? "—"} ·{" "}
            {new Date(review.created_at).toLocaleString("pl-PL")}
            {review.page_count ? ` · ${review.page_count} str.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {review.findings_count > 0 && (
            <span className="text-xs text-[var(--color-text-secondary)]">
              {review.findings_count} uwag
            </span>
          )}
          <Tooltip content={REVIEW_STATUS_DESC[review.status]}>
            <Badge color={REVIEW_STATUS_COLOR[review.status]}>
              {REVIEW_STATUS_LABEL[review.status]}
            </Badge>
          </Tooltip>
          {(review.status === "uploaded" || review.status === "error") && (
            <Button
              size="sm"
              variant="secondary"
              loading={startReview.isPending}
              onClick={(e) => {
                e.preventDefault();
                startReview.mutate(review.id);
              }}
            >
              Analizuj
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
}

export function EngAronReviewsPage() {
  const user = useAuthStore((s) => s.user);
  const isReviewer =
    !!user && (user.is_ceo || (user.role === "director" && user.is_technical_director));

  const [tab, setTab] = useState<"all" | "mine">("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [q, setQ] = useState("");
  const reviews = useReviews({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(stateFilter ? { review_state: stateFilter } : {}),
    ...(q ? { q } : {}),
  });
  // Pełna lista bez filtrów — źródło liczników legendy (ile recenzji jest
  // aktualnie w danym stanie). Przy pustych filtrach współdzieli cache z listą.
  const allReviews = useReviews({});
  const mine = useMyReviews(isReviewer && tab === "mine");

  const toReviewCount = mine.data?.to_review.length ?? 0;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
            Recenzje opracowań
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Wgraj PDF opracowania — ENG-ARON sprawdzi je względem bazy wiedzy i
            przygotuje numerowane uwagi.
          </p>
        </div>
        <Button size="sm" onClick={() => setUploadOpen(true)}>
          <Plus size={14} className="mr-1.5" /> Wgraj opracowanie
        </Button>
      </div>

      {isReviewer && (
        <div className="flex gap-1 border-b border-[var(--color-border)]">
          <button
            onClick={() => setTab("all")}
            className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors ${
              tab === "all"
                ? "border-[var(--color-primary)] text-[var(--color-text-primary)] font-medium"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            }`}
          >
            Wszystkie
          </button>
          <button
            onClick={() => setTab("mine")}
            className={`px-3 py-1.5 text-sm border-b-2 -mb-px transition-colors ${
              tab === "mine"
                ? "border-[var(--color-primary)] text-[var(--color-text-primary)] font-medium"
                : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
            }`}
          >
            Moje recenzje
            {toReviewCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] px-1 text-[10px] font-semibold bg-[var(--color-primary)] text-white rounded-full">
                {toReviewCount}
              </span>
            )}
          </button>
        </div>
      )}

      {tab === "all" ? (
        <>
          <StatusLegend
            reviews={allReviews.data ?? []}
            stateFilter={stateFilter}
            statusFilter={statusFilter}
            onStateFilter={setStateFilter}
            onStatusFilter={setStatusFilter}
          />
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <Input placeholder="Szukaj po nazwie…" value={q} onChange={(e) => setQ(e.target.value)} />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: "", label: "Status: wszystkie" },
                ...Object.entries(REVIEW_STATUS_LABEL).map(([value, label]) => ({ value, label })),
              ]}
            />
          </div>

          {reviews.isLoading ? (
            <PageLoader />
          ) : (reviews.data ?? []).length === 0 ? (
            <div className="border border-dashed border-[var(--color-border-strong)] p-10 text-center space-y-2">
              <FileSearch size={28} className="mx-auto text-[var(--color-text-muted)]" />
              <p className="text-sm text-[var(--color-text-secondary)]">
                {statusFilter || stateFilter || q
                  ? "Brak recenzji spełniających wybrany filtr."
                  : "Brak recenzji — wgraj pierwsze opracowanie."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {(reviews.data ?? []).map((r) => (
                <ReviewRow key={r.id} review={r} />
              ))}
            </div>
          )}
        </>
      ) : (
        <MyReviewsView
          loading={mine.isLoading}
          toReview={mine.data?.to_review ?? []}
          done={mine.data?.done ?? []}
        />
      )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}

function MyReviewsView({
  loading,
  toReview,
  done,
}: {
  loading: boolean;
  toReview: EngReview[];
  done: EngReview[];
}) {
  if (loading) return <PageLoader />;
  if (toReview.length === 0 && done.length === 0) {
    return (
      <div className="border border-dashed border-[var(--color-border-strong)] p-10 text-center space-y-2">
        <FileSearch size={28} className="mx-auto text-[var(--color-text-muted)]" />
        <p className="text-sm text-[var(--color-text-secondary)]">
          Nie masz recenzji przypisanych do Ciebie.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-[var(--color-text-primary)]">
          Do recenzji ({toReview.length})
        </h2>
        {toReview.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">Nic nie czeka na Twoją decyzję.</p>
        ) : (
          toReview.map((r) => <ReviewRow key={r.id} review={r} />)
        )}
      </section>
      {done.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-[var(--color-text-primary)]">
            Zrecenzowane ({done.length})
          </h2>
          {done.map((r) => (
            <ReviewRow key={r.id} review={r} />
          ))}
        </section>
      )}
    </div>
  );
}
